import { MEDIA_TYPE_IMAGE } from './constants';

/**
 * Product rules for the creator hand-off from an overlay template. Keeping
 * these in one place makes the deliberately different creator experience
 * explicit instead of scattering route checks throughout the upload UI.
 */
export const OVERLAY_TEMPLATE_CREATOR_BEHAVIOUR = {
  allPlatformsOnly: true,
  imageUploadsOnly: true,
  showTemplatePreviewBeforeMedia: true,
  disablePlatformPreviewActions: true,
  disableAutomaticMultiImageEditor: true,
  requireTemplateSaveBeforeExit: true,
  allowTemplateChange: true,
  applyToFirstMediaOnly: true
} as const;

type OverlayTemplateCreatorMedia = {
  /** Identifies the upload; positions are not stable across filtered lists. */
  uuid?: string;
  media_type?: string | null;
  uploaded?: boolean;
  editorUpdateState?: boolean;
  overlay_template_uuid?: string | null;
};

/**
 * The uploads that should still be taken through the overlay.
 *
 * An overlay belongs to the post's first image. Once it has been put on one,
 * everything uploaded afterwards takes the ordinary path — the overlay is a
 * starting point for the post, not a treatment applied to every image that
 * follows it.
 */
export const getOverlayTemplateEligibleMedia = <
  Media extends OverlayTemplateCreatorMedia
>(
  media: Media[] | undefined | null
): Media[] => {
  const images = (media ?? []).filter(
    (item) =>
      String(item?.media_type ?? '').toLowerCase() === MEDIA_TYPE_IMAGE &&
      Boolean(item?.uploaded)
  );

  if (
    OVERLAY_TEMPLATE_CREATOR_BEHAVIOUR.applyToFirstMediaOnly &&
    images.some((item) => Boolean(item?.overlay_template_uuid))
  )
    return [];

  const awaitingOverlay = images.filter(
    (item) => Boolean(item?.editorUpdateState)
  );

  return OVERLAY_TEMPLATE_CREATOR_BEHAVIOUR.applyToFirstMediaOnly
    ? awaitingOverlay.slice(0, 1)
    : awaitingOverlay;
};

/**
 * Whether the overlay has already been put on one of these uploads.
 *
 * Deliberately says nothing about upload progress: it answers "is the overlay
 * still to come?", which has to hold from the moment a file is picked, long
 * before it finishes uploading and counts as eligible.
 */
export const hasOverlayBeenPlaced = (
  media: OverlayTemplateCreatorMedia[] | undefined | null
): boolean =>
  (media ?? []).some((item) => Boolean(item?.overlay_template_uuid));

/**
 * Images an overlay can be put on by hand.
 *
 * A separate question from `getOverlayTemplateEligibleMedia`, which answers
 * who the automatic hand-off owes the overlay to and therefore stops once it
 * has been placed. Choosing an overlay from the editor is not that hand-off:
 * it stays available afterwards, and applies to an image that has already been
 * edited just as well as to a fresh one.
 */
export const getOverlayApplicableMedia = <
  Media extends OverlayTemplateCreatorMedia
>(
  media: Media[] | undefined | null
): Media[] =>
  (media ?? []).filter(
    (item) =>
      String(item?.media_type ?? '').toLowerCase() === MEDIA_TYPE_IMAGE &&
      Boolean(item?.uploaded)
  );
